import React, { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2 } from "lucide-react";

export interface ExcludeFieldOption {
  key: string;
  label: string;
}

export interface SectionItemOption {
  id: string | number;
  label: string;
}

export const DEFAULT_EXCLUDE_FIELDS: ExcludeFieldOption[] = [
  { key: "avatar", label: "Profile Photo" },
  { key: "job_role", label: "Job Role" },
  { key: "experience_years", label: "Experience (Years)" },
  { key: "experience_months", label: "Experience (Months)" },
  { key: "summary", label: "Summary" },
  { key: "skills", label: "Skills" },
  { key: "educations", label: "Education" },
  { key: "projects", label: "Projects" },
  { key: "certifications", label: "Certifications" },
  { key: "awards", label: "Awards" },
];

export type SectionKey =
  | "projects"
  | "skills"
  | "educations"
  | "certifications"
  | "awards";

export interface ExcludeFieldsSelectorProps {
  title?: string;
  fields?: ExcludeFieldOption[];
  values: Record<string, boolean>;
  onChange: (key: string, checked: boolean) => void;
  className?: string;
  isSingleCandidate?: boolean;
  sectionItems?: Partial<Record<SectionKey, SectionItemOption[]>>;
  sectionTotalItems?: Partial<Record<SectionKey, number>>;
  excludedItemIds?: Partial<Record<SectionKey, (string | number)[]>>;
  onItemExcludeChange?: (
    section: SectionKey,
    itemId: string | number,
    checked: boolean
  ) => void;
  onLoadMore?: (section: SectionKey) => void;
  sectionHasMore?: Partial<Record<SectionKey, boolean>>;
  isFetchingSection?: Partial<Record<SectionKey, boolean>>;
}

export function ExcludeFieldsSelector({
  title = "Exclude Information",
  fields = DEFAULT_EXCLUDE_FIELDS,
  values,
  onChange,
  className = "",
  isSingleCandidate = false,
  sectionItems = {},
  sectionTotalItems = {},
  excludedItemIds = {},
  onItemExcludeChange,
  onLoadMore,
  sectionHasMore = {},
  isFetchingSection = {},
}: ExcludeFieldsSelectorProps) {
  const multiSectionKeys: SectionKey[] = [
    "projects",
    "skills",
    "educations",
    "certifications",
    "awards",
  ];

  // Per-section visible items count (starts at 10)
  const [visibleCounts, setVisibleCounts] = useState<
    Record<SectionKey, number>
  >({
    projects: 10,
    skills: 10,
    educations: 10,
    certifications: 10,
    awards: 10,
  });

  const handleScroll = (
    section: SectionKey,
    e: React.UIEvent<HTMLDivElement>
  ) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 40) {
      setVisibleCounts((prev) => ({
        ...prev,
        [section]: (prev[section] || 10) + 10,
      }));
      onLoadMore?.(section);
    }
  };

  return (
    <div className={className}>
      {title && (
        <label className="text-sm font-semibold mb-3 block text-slate-900 dark:text-slate-100">
          {title}
        </label>
      )}
      <div className="flex flex-col gap-3.5">
        {fields.map((field) => {
          const isMultiSection = multiSectionKeys.includes(
            field.key as SectionKey
          );
          const items = isMultiSection
            ? sectionItems[field.key as SectionKey] || []
            : [];
          const currentExcludedIds = isMultiSection
            ? excludedItemIds[field.key as SectionKey] || []
            : [];

          if (isSingleCandidate && isMultiSection && items.length > 0) {
            const allExcluded =
              Boolean(values[field.key]) ||
              (items.length > 0 &&
                items.every((item) => currentExcludedIds.includes(item.id)));

            const visibleLimit = visibleCounts[field.key as SectionKey] || 10;
            const visibleItems = items.slice(0, visibleLimit);
            const hasMore = items.length > visibleLimit;

            return (
              <div key={field.key} className="flex flex-col gap-2">
                <label className="flex items-center gap-2.5 text-sm text-slate-800 dark:text-slate-200 font-semibold select-none cursor-pointer">
                  <Checkbox
                    checked={allExcluded}
                    onCheckedChange={(checked) => {
                      const isChecked = Boolean(checked);
                      onChange(field.key, isChecked);
                      items.forEach((item) => {
                        onItemExcludeChange?.(
                          field.key as SectionKey,
                          item.id,
                          isChecked
                        );
                      });
                    }}
                  />
                  <span>
                    {field.label} (
                    {sectionTotalItems[field.key as SectionKey] ?? items.length}
                    )
                  </span>
                </label>

                {/* Scrollable Container with max-h-44 and infinite load on scroll */}
                <div
                  onScroll={(e) => handleScroll(field.key as SectionKey, e)}
                  className="ml-6 flex flex-col gap-2 border-l-2 border-slate-200 dark:border-slate-800 pl-3 py-1 max-h-44 overflow-y-auto"
                >
                  {visibleItems.map((item) => {
                    const isItemExcluded = currentExcludedIds.includes(
                      item.id
                    );
                    return (
                      <label
                        key={item.id}
                        className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400 cursor-pointer font-medium select-none"
                      >
                        <Checkbox
                          checked={isItemExcluded}
                          onCheckedChange={(checked) =>
                            onItemExcludeChange?.(
                              field.key as SectionKey,
                              item.id,
                              Boolean(checked)
                            )
                          }
                        />
                        <span className="truncate">{item.label}</span>
                      </label>
                    );
                  })}
                  {isFetchingSection[field.key as SectionKey] && (
                    <div className="flex items-center gap-1.5 text-xs text-purple-600 dark:text-purple-400 font-medium py-1">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Loading more...</span>
                    </div>
                  )}
                  {!isFetchingSection[field.key as SectionKey] &&
                    (hasMore || sectionHasMore[field.key as SectionKey]) && (
                      <div className="text-[11px] text-slate-400 font-medium py-1">
                        Scroll to load more...
                      </div>
                    )}
                </div>
              </div>
            );
          }

          return (
            <label
              key={field.key}
              className="flex items-center gap-2.5 text-sm text-slate-700 dark:text-slate-300 cursor-pointer font-medium select-none"
            >
              <Checkbox
                checked={Boolean(values[field.key])}
                onCheckedChange={(checked) =>
                  onChange(field.key, Boolean(checked))
                }
              />
              {field.label}
            </label>
          );
        })}
      </div>
    </div>
  );
}

export default ExcludeFieldsSelector;
